import React, { useState, useEffect } from 'react';
import axios from '../api/client';
import { useAuth } from '../context/AuthContext';
 
export default function ChangeManagement() {
  const { user } = useAuth();
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedChange, setSelectedChange] = useState(null);
  const [approvalLogs, setApprovalLogs] = useState([]);
  const [showAuditTrail, setShowAuditTrail] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusChangeData, setStatusChangeData] = useState({
    changeId: '',
    changeTitle: '',
    currentStatus: '',
    newStatus: '',
    comments: ''
  });
  const [formData, setFormData] = useState({
    change_type: 'procedure',
    title: '',
    description: '',
    justification: '',
    requested_by: '',
    impact_level: 'medium',
    target_implementation_date: ''
  });
 
  useEffect(() => {
    fetchChanges();
  }, []);
 
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        requested_by: user.name || user.id || 'user-1'
      }));
    }
  }, [user]);
 
  const fetchChanges = async () => {
    try {
      const response = await axios.get('/changes');
      setChanges(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching changes:', error);
      setLoading(false);
    }
  };
 
  const fetchApprovalLog = async (changeId) => {
    try {
      const response = await axios.get(`/changes/${changeId}/approval-log`);
      setApprovalLogs(response.data);
    } catch (error) {
      console.error('Error fetching approval log:', error);
      setApprovalLogs([]);
    }
  };
 
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/changes', formData);
      alert('Change request created successfully!');
      setShowCreateForm(false);
      setFormData({
        change_type: 'procedure',
        title: '',
        description: '',
        justification: '',
        requested_by: user?.name || user?.id || 'user-1',
        impact_level: 'medium',
        target_implementation_date: ''
      });
      fetchChanges();
    } catch (error) {
      console.error('Error creating change request:', error);
      alert('Error creating change request');
    }
  };
 
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
 
  const openStatusModal = (change, newStatus) => {
    setStatusChangeData({
      changeId: change.id,
      changeTitle: change.title,
      currentStatus: change.status,
      newStatus: newStatus,
      comments: ''
    });
    setShowStatusModal(true);
  };
 
  const confirmStatusChange = async () => {
    try {
      const params = new URLSearchParams({ status: statusChangeData.newStatus });
      if (statusChangeData.comments) {
        params.append('comments', statusChangeData.comments);
      }
      await axios.patch(`/changes/${statusChangeData.changeId}/progress?${params.toString()}`);
      alert('Status updated and logged successfully!');
      setShowStatusModal(false);
      setStatusChangeData({ changeId: '', changeTitle: '', currentStatus: '', newStatus: '', comments: '' });
      fetchChanges();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating status');
    }
  };
 
  const openAuditTrail = async (change) => {
    setSelectedChange(change);
    await fetchApprovalLog(change.id);
    setShowAuditTrail(true);
  };
 
  const addReviewComment = async (changeId) => {
    const comment = prompt('Enter your review comment:');
    if (!comment) return;
    try {
      await axios.post(`/changes/${changeId}/approval-log`, {
        change_request_id: changeId,
        action: 'comment_added',
        performed_by_id: user?.id || 'unknown',
        performed_by_name: user?.name || 'Unknown',
        performed_by_role: user?.role || 'unknown',
        comments: comment,
      });
      alert('Comment logged successfully!');
      await fetchApprovalLog(changeId);
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Error adding comment');
    }
  };
 
  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'review': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'implementing': return 'bg-blue-100 text-blue-800';
      case 'complete': return 'bg-purple-100 text-purple-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
 
  const getImpactColor = (level) => {
    switch (level) {
      case 'low': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'high': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
 
  const getActionColor = (action) => {
    switch (action) {
      case 'approved': return 'text-green-700 bg-green-50';
      case 'rejected': return 'text-red-700 bg-red-50';
      case 'reviewed': return 'text-yellow-700 bg-yellow-50';
      case 'submitted': return 'text-blue-700 bg-blue-50';
      case 'completed': return 'text-purple-700 bg-purple-50';
      case 'implementation_started': return 'text-indigo-700 bg-indigo-50';
      case 'comment_added': return 'text-gray-700 bg-gray-50';
      default: return 'text-gray-700 bg-gray-50';
    }
  };
 
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };
 
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const d = new Date(dateString);
    return d.toLocaleDateString() + ' ' + d.toLocaleTimeString();
  };
 
  if (loading) {
    return (
      <div className="p-8">
        <div className="text-center">Loading change requests...</div>
      </div>
    );
  }
 
  return (
    <div className="p-8">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Change Management</h1>
          <p className="text-gray-600 mt-2">
            Track and manage organizational changes with full traceability
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 font-semibold"
        >
          + New Change Request
        </button>
      </div>
 
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-gray-500 text-sm font-medium">Total Changes</div>
          <div className="text-3xl font-bold mt-2">{changes.length}</div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-gray-500 text-sm font-medium">In Review</div>
          <div className="text-3xl font-bold mt-2 text-yellow-600">
            {changes.filter(c => c.status === 'review').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-gray-500 text-sm font-medium">Approved</div>
          <div className="text-3xl font-bold mt-2 text-green-600">
            {changes.filter(c => c.status === 'approved').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-gray-500 text-sm font-medium">Implementing</div>
          <div className="text-3xl font-bold mt-2 text-blue-600">
            {changes.filter(c => c.status === 'implementing').length}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-gray-500 text-sm font-medium">Completed</div>
          <div className="text-3xl font-bold mt-2 text-purple-600">
            {changes.filter(c => c.status === 'complete').length}
          </div>
        </div>
      </div>
 
      {/* Change Requests Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h2 className="text-lg font-semibold text-gray-800">Change Requests</h2>
          <p className="text-sm text-gray-500">All status changes are logged with user, timestamp, and comments</p>
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Change
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Impact
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Request Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Update Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Audit Trail
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {changes.map((change) => (
              <tr key={change.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900">{change.title}</div>
                  <div className="text-sm text-gray-500">{change.description?.substring(0, 60)}...</div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900 capitalize">{change.change_type}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getImpactColor(change.impact_level)}`}>
                    {change.impact_level}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(change.status)}`}>
                    {change.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-900">{formatDate(change.request_date)}</td>
                <td className="px-6 py-4 text-sm">
                  <select
                    value={change.status}
                    onChange={(e) => {
                      if (e.target.value !== change.status) {
                        openStatusModal(change, e.target.value);
                      }
                    }}
                    className="border border-gray-300 rounded px-2 py-1 text-sm"
                  >
                    <option value="draft">Draft</option>
                    <option value="review">Review</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                    <option value="implementing">Implementing</option>
                    <option value="complete">Complete</option>
                  </select>
                </td>
                <td className="px-6 py-4 text-sm">
                  <button
                    onClick={() => openAuditTrail(change)}
                    className="text-blue-600 hover:text-blue-800 font-medium underline"
                  >
                    View Log
                  </button>
                </td>
              </tr>
            ))}
            {changes.length === 0 && (
              <tr>
                <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                  No change requests found. Create a new one to get started.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
 
      {/* Status Change Confirmation Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold">Confirm Status Change</h2>
              <p className="text-sm text-gray-500 mt-1">
                This action will be logged with your identity and timestamp
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-gray-600">
                  <strong>Change Request:</strong> {statusChangeData.changeTitle}
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(statusChangeData.currentStatus)}`}>
                  {statusChangeData.currentStatus}
                </span>
                <span className="text-gray-400">&rarr;</span>
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(statusChangeData.newStatus)}`}>
                  {statusChangeData.newStatus}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">
                  <strong>Performed by:</strong> {user?.name || user?.id || 'Unknown'} ({user?.role || 'unknown'})
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Comments / Justification *
                </label>
                <textarea
                  value={statusChangeData.comments}
                  onChange={(e) => setStatusChangeData(prev => ({ ...prev, comments: e.target.value }))}
                  rows="3"
                  className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Provide a reason for this status change..."
                  required
                />
              </div>
            </div>
            <div className="flex space-x-4 p-6 border-t border-gray-200">
              <button
                onClick={confirmStatusChange}
                disabled={!statusChangeData.comments.trim()}
                className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Confirm &amp; Log Change
              </button>
              <button
                type="button"
                onClick={() => setShowStatusModal(false)}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400 font-semibold"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
 
      {/* Audit Trail Modal */}
      {showAuditTrail && selectedChange && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold">Approval Audit Trail</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedChange.title} &mdash; Full traceability log
                  </p>
                </div>
                <button
                  onClick={() => setShowAuditTrail(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  &times;
                </button>
              </div>
            </div>
            <div className="p-6">
              {/* Change Summary */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Type:</span>{' '}
                    <span className="font-medium capitalize">{selectedChange.change_type}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Impact:</span>{' '}
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getImpactColor(selectedChange.impact_level)}`}>
                      {selectedChange.impact_level}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Current Status:</span>{' '}
                    <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusColor(selectedChange.status)}`}>
                      {selectedChange.status}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Requested:</span>{' '}
                    <span className="font-medium">{formatDate(selectedChange.request_date)}</span>
                  </div>
                </div>
              </div>
 
              {/* Timeline */}
              <h3 className="text-lg font-semibold mb-4">Activity Timeline</h3>
              {approvalLogs.length === 0 ? (
                <p className="text-gray-500 text-sm">No audit trail entries found for this change request.</p>
              ) : (
                <div className="space-y-0">
                  {approvalLogs.map((log, index) => (
                    <div key={log.id || index} className="relative pl-8 pb-6">
                      {/* Timeline line */}
                      {index < approvalLogs.length - 1 && (
                        <div className="absolute left-3 top-6 bottom-0 w-0.5 bg-gray-200"></div>
                      )}
                      {/* Timeline dot */}
                      <div className={`absolute left-1 top-1.5 w-5 h-5 rounded-full border-2 border-white shadow ${
                        log.action === 'approved' ? 'bg-green-500' :
                        log.action === 'rejected' ? 'bg-red-500' :
                        log.action === 'submitted' ? 'bg-blue-500' :
                        log.action === 'completed' ? 'bg-purple-500' :
                        'bg-gray-400'
                      }`}></div>
                      {/* Content */}
                      <div className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <span className={`px-2 py-0.5 text-xs font-semibold rounded ${getActionColor(log.action)}`}>
                              {log.action?.replace(/_/g, ' ').toUpperCase()}
                            </span>
                            {log.from_status && log.to_status && (
                              <span className="text-xs text-gray-400 ml-2">
                                {log.from_status} &rarr; {log.to_status}
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-gray-400">
                            {formatDateTime(log.timestamp)}
                          </span>
                        </div>
                        <div className="text-sm">
                          <p className="text-gray-800">
                            <strong>{log.performed_by_name}</strong>
                            {log.performed_by_role && (
                              <span className="text-gray-500 ml-1">({log.performed_by_role})</span>
                            )}
                          </p>
                          {log.comments && (
                            <p className="text-gray-600 mt-1 italic">"{log.comments}"</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
 
              {/* Add Comment Button */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => addReviewComment(selectedChange.id)}
                  className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 font-medium text-sm"
                >
                  + Add Review Comment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
 
      {/* Create Form Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold">New Change Request</h2>
              <p className="text-sm text-gray-500 mt-1">This submission will be logged in the audit trail</p>
            </div>
            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Change Type *
                  </label>
                  <select
                    name="change_type"
                    value={formData.change_type}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="policy">Policy</option>
                    <option value="procedure">Procedure</option>
                    <option value="system">System</option>
                    <option value="other">Other</option>
                  </select>
                </div>
 
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
 
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
 
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Justification *
                  </label>
                  <textarea
                    name="justification"
                    value={formData.justification}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
 
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Impact Level *
                  </label>
                  <select
                    name="impact_level"
                    value={formData.impact_level}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
 
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Target Implementation Date
                  </label>
                  <input
                    type="date"
                    name="target_implementation_date"
                    value={formData.target_implementation_date}
                    onChange={handleInputChange}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
 
              <div className="flex space-x-4 mt-6">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 font-semibold"
                >
                  Create Change Request
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="flex-1 bg-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-400 font-semibold"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
